<script setup>
  import { computed, watch, useTemplateRef } from 'vue';
  import { useResizeObserver } from '@vueuse/core';

  const props = defineProps({
    // The widget's real size; the preview scales it down to fit its box
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    }
  });

  // The scale the widget is drawn at (1 = full size)
  const scale = defineModel('scale', { type: Number, default: 1 });

  const previewElement = useTemplateRef('preview-element');

  const previewStyles = computed(() => ({
    '--jwx-widget-preview-width': `${props.width}px`,
    '--jwx-widget-preview-height': `${props.height}px`,
    '--jwx-widget-preview-scale': scale.value
  }));

  // Fit the widget inside the preview's padding on both axes, never scaling it up
  function setScale() {
    if (!previewElement.value) return;

    const { paddingTop, paddingRight, paddingBottom, paddingLeft } = getComputedStyle(previewElement.value);
    const availableWidth = previewElement.value.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
    const availableHeight = previewElement.value.clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);

    if (availableWidth <= 0 || availableHeight <= 0) return;

    scale.value = Math.min(availableWidth / props.width, availableHeight / props.height, 1);
  }

  useResizeObserver(previewElement, setScale);

  // The widget's own size changes too (in the editor), not just the preview box
  watch(() => [props.width, props.height], setScale);
</script>

<template>
  <!-- Draws the widget at its real pixel size, scaled to fit this box -->
  <div ref="preview-element" class="jwx-widget-preview" :style="previewStyles">
    <div class="jwx-widget-preview__frame">
      <slot></slot>
    </div>
  </div>
</template>
